import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineSearch, HiOutlinePhone, HiOutlineLocationMarker, HiExternalLink } from 'react-icons/hi';
import { MdOutlineLocalHospital, MdOutlineBusiness, MdOutlineHealthAndSafety, MdOutlineMap } from 'react-icons/md';

const FindHelp = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('All');

  const facilities = [
    { name: 'National Eye Hospital', type: 'Government Hospitals', location: 'Colombo 10', phone: '+94 11 269 3911', icon: <MdOutlineHealthAndSafety />, desc: 'Primary government institution for advanced ophthalmic diagnostics and specialty surgery.' },
    { name: 'Kandy National Eye Unit', type: 'Government Hospitals', location: 'Kandy', phone: '+94 81 223 3337', icon: <MdOutlineHealthAndSafety />, desc: 'Specialized regional center providing comprehensive eye care to the Central Province.' },
    { name: 'Nawaloka Eye Center', type: 'Eye Units in private Hospitals', location: 'Colombo 02', phone: '+94 11 254 4444', icon: <MdOutlineLocalHospital />, desc: 'State-of-the-art private clinical facilities with 24/7 emergency ophthalmic support.' },
    { name: 'Asiri Surgical Eye', type: 'Eye Units in private Hospitals', location: 'Colombo 05', phone: '+94 11 452 4400', icon: <MdOutlineLocalHospital />, desc: 'Advanced surgical suites specialized in retinal and refractive ocular procedures.' },
    { name: 'Vision Care Optical', type: 'Private Eye Clinics', location: 'Islandwide', phone: '+94 11 239 1010', icon: <MdOutlineBusiness />, desc: 'Extensive network providing preliminary testing and vision correction solutions.' },
    { name: 'Golden Key Hospital', type: 'Private Eye Clinics', location: 'Rajagiriya', phone: '+94 11 288 0288', icon: <MdOutlineBusiness />, desc: 'Exclusive private facility specializing in comprehensive eye and ENT care.' }
  ];

  const categories = ['All', 'Government Hospitals', 'Eye Units in private Hospitals', 'Private Eye Clinics'];

  const filtered = facilities.filter(f => 
    (activeTab === 'All' || f.type === activeTab) &&
    (f.name.toLowerCase().includes(searchTerm.toLowerCase()) || f.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="pt-40 pb-24 px-4 min-h-screen bg-black mesh-gradient">
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Luxury Header */}
        <div className="text-center mb-16 space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl shadow-sm backdrop-blur-md">
            <MdOutlineMap className="text-[var(--gold-primary)]" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Clinical Network v2.4</span>
          </div>
          <h1 className="text-4xl md:text-7xl font-black text-white tracking-tighter uppercase italic">
            Find <span className="text-gradient">Help</span>
          </h1>
          <h2 className="text-xl font-bold text-slate-300 uppercase tracking-widest">Find Eye Care Services Near You</h2>
          <p className="text-slate-400 font-medium max-w-2xl mx-auto text-lg leading-relaxed italic">
            If you are experiencing vision problems or need professional care, you can contact the following hospitals and eye clinics for assistance.
          </p>
        </div>

        {/* Luxury Search & Filters */}
        <div className="mb-20 space-y-12">
          <div className="max-w-3xl mx-auto relative group">
            <HiOutlineSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-600 text-2xl group-focus-within:text-[var(--gold-primary)] transition-all duration-500" />
            <input 
              type="text" placeholder="Search by name, city, or clinical specialty..."
              className="w-full pl-16 pr-8 py-7 bg-black/40 border-2 border-white/5 rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.3)] outline-none focus:border-[var(--gold-primary)] focus:bg-black transition-all text-lg font-black text-white placeholder:text-slate-800 tracking-tight"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-0 rounded-3xl bg-[var(--gold-primary)]/5 -z-10 blur-2xl opacity-0 group-focus-within:opacity-100 transition-opacity" />
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {categories.map(cat => (
              <button
                key={cat} onClick={() => setActiveTab(cat)}
                className={`px-10 py-4 rounded-2xl text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-700 ${
                  activeTab === cat ? 'bg-[var(--gold-primary)] text-black shadow-[0_0_30px_var(--gold-glow)] scale-105' : 'bg-white/5 text-slate-500 border border-white/5 hover:border-white/10 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Results Grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filtered.map((f, idx) => (
              <motion.div
                key={f.name} layout
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.6, delay: idx * 0.05 }}
                className="group gold-card p-10 flex flex-col justify-between overflow-hidden relative"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-[var(--gold-primary)]/10 blur-[60px] -mr-16 -mt-16 opacity-0 group-hover:opacity-100 transition-all duration-700`} />
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-10">
                    <div className="w-16 h-16 bg-black text-[var(--gold-primary)] rounded-2xl flex items-center justify-center text-3xl group-hover:bg-[var(--gold-primary)] group-hover:text-black group-hover:rotate-12 transition-all duration-700 shadow-[inset_0_0_20px_rgba(255,255,255,0.05)]">
                      {f.icon}
                    </div>
                    <span className="text-[8px] font-black uppercase tracking-[0.3em] bg-[var(--gold-primary)]/10 text-[var(--gold-primary)] px-4 py-2 rounded-xl border border-[var(--gold-primary)]/20 italic">
                      {f.type.split(' ')[0]}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-black text-white mb-4 group-hover:text-[var(--gold-primary)] transition-colors uppercase tracking-tighter">{f.name}</h3>
                  <p className="text-xs font-medium text-slate-500 leading-relaxed mb-10 italic">{f.desc}</p>
                </div>

                <div className="relative z-10 border-t border-white/5 pt-10 space-y-5">
                  <div className="flex items-center gap-4 group/item">
                    <div className="w-10 h-10 rounded-xl bg-white/5 text-[var(--gold-primary)] flex items-center justify-center border border-white/10 group-hover:border-[var(--gold-primary)]/50 transition-colors"><HiOutlineLocationMarker size={20} /></div>
                    <span className="text-sm font-black text-slate-400 uppercase tracking-tight">{f.location}</span>
                  </div>
                  <div className="flex items-center gap-4 group/item">
                    <div className="w-10 h-10 rounded-xl bg-white/5 text-[var(--gold-primary)] flex items-center justify-center border border-white/10 group-hover:border-[var(--gold-primary)]/50 transition-colors"><HiOutlinePhone size={20} /></div>
                    <span className="text-sm font-black text-white italic">{f.phone}</span>
                  </div>
                  
                  <button className="w-full mt-8 py-5 bg-black text-slate-600 group-hover:bg-[var(--gold-primary)] group-hover:text-black rounded-2xl font-black text-[10px] tracking-[0.3em] flex items-center justify-center gap-3 transition-all duration-700 uppercase">
                    VIEW ON MAP <HiExternalLink size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-40">
            <div className="text-6xl mb-6 grayscale opacity-30">🏥</div>
            <h3 className="text-2xl font-black text-slate-900 mb-2">No results found</h3>
            <p className="text-slate-400 font-medium italic">Adjust your search criteria or clear your filters.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FindHelp;
